import { getToken } from 'src/store/utils/balances/core/helpers';

export interface MergedTokenGroup {
  /** The mapped label that several distinct raw symbols collapse into. */
  label: string;
  /** The distinct raw token symbols (uppercased) merged into `label`. */
  symbols: string[];
}

// Minimal structural shape this helper reads. `JsonBalance[]` from the store is
// assignable to this, but narrowing the input keeps the util decoupled and
// trivially testable without constructing full balance objects.
interface TokenBearingBalance {
  assets?: { token: string }[];
}

/**
 * Detects tokens that the token map collapses into a single displayed label.
 *
 * A "merge" is a mapped label that at least two *distinct* raw symbols — both
 * present in the given balances — resolve to via {@link getToken}. This is used
 * to warn the user that token mapping is hiding the fact that separate tokens
 * were combined (e.g. `XION` mapped onto `VERONA`).
 *
 * When `tokenMap` is empty (mapping disabled) every symbol resolves to itself,
 * so no merges are ever reported.
 *
 * @returns the merge groups, sorted by label, each with its symbols sorted.
 */
export const getMergedTokenGroups = ({
  balances = [],
  tokenMap = {},
}: {
  balances?: TokenBearingBalance[];
  tokenMap?: Record<string, string>;
}): MergedTokenGroup[] => {
  const symbolsByLabel = new Map<string, Set<string>>();

  for (const balance of balances) {
    for (const asset of balance.assets ?? []) {
      const label = getToken(asset.token, tokenMap);
      const symbols = symbolsByLabel.get(label) ?? new Set<string>();
      symbols.add(asset.token.toUpperCase());
      symbolsByLabel.set(label, symbols);
    }
  }

  return [...symbolsByLabel.entries()]
    .filter(([, symbols]) => symbols.size >= 2)
    .map(([label, symbols]) => ({ label, symbols: [...symbols].sort() }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
